import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useParameterizedQuery, useMutation } from 'react-fetching-library';
import { getEmployeesAction, updateEmployeeConfigAction } from '../../../services/request';
import toast from 'react-hot-toast';

import { setEOrderColumnsWidth, setEOrderColumnsSequence } from '../../../services/redux-actions';
import { EmployeesColumns } from '../../../services/column-definitions';

const EmployeesContext = React.createContext();

export function useEmployees() {
  return useContext(EmployeesContext);
}

export default function EmployeesProvider({ children }) {
  const dispatch = useDispatch();
  const { eColumnsWidth, eColumnsSequence } = useSelector(state => state.employeeRecruitmentConfig);
  const { employeeId, employeeConfigId } = useSelector(state => state.app);
  const { mutate } = useMutation(updateEmployeeConfigAction);
  const [search, setSearch] = useState(null);
  const [filtersParams, setFiltersParams] = useState(null);
  const { loading, payload, error, query } = useQuery(getEmployeesAction('GET', null, search, filtersParams));
  const { loading: optionsLoading, payload: optionsPayload, error: optionsError } = useQuery(getEmployeesAction('OPTIONS'));
  const { query: getEmployeeDetails } = useParameterizedQuery(getEmployeesAction);
  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState({});
  const [columns, setColumns] = useState(EmployeesColumns());
  const [choices, setChoices] = useState({});

  const handleChangeStateCallback = newState => {
    setEmployee({ ...employee, state: newState });
  };

  useEffect(() => {
    if (!error && !loading) {
      setEmployees(payload.results.filter(e => e.user == null));
    }
  }, [loading]);

  const getDetails = async id => {
    const { payload, loading, error } = await getEmployeeDetails('GET', id);

    if (!error && !loading) {
      return payload;
    }

    if (error) {
      toast.error('Nie udało się pobrać danych pracownika');
    }

    return {};
  };

  const handleColumnWidthChange = async colDef => {
    const { payload } = dispatch(setEOrderColumnsWidth({ ...eColumnsWidth, [colDef.field]: colDef.width }));
    await mutate({ e_columns_width: payload, employee: employeeId }, employeeConfigId);
  };

  const handleColumnsOrderChange = async columns => {
    const { payload } = dispatch(setEOrderColumnsSequence(columns));
    await mutate({ e_columns_sequence: payload, employee: employeeId }, employeeConfigId);
  };

  useEffect(() => {
    setColumns(prev => prev.map(c => {
      if (c.field in eColumnsWidth) {
        return { ...c, width: eColumnsWidth[c.field] };
      }

      return c;
    }));
  }, [eColumnsWidth]);

  useEffect(() => {
    setColumns(prev => [...prev].sort((a, b) => eColumnsSequence.indexOf(a.field) - eColumnsSequence.indexOf(b.field)));
  }, [eColumnsSequence]);

  useEffect(() => {
    if (!optionsError && !optionsLoading) {
      setChoices(optionsPayload.actions.GET.choices);
    }
  }, [optionsLoading]);

  return (
    <EmployeesContext.Provider value={
      {
        rowData: employee,
        setRowData: setEmployee,
        rows: employees,
        setRows: setEmployees,
        columns,
        getDetails,
        setSearch,
        setFiltersParams,
        handleChangeStateCallback,
        handleColumnWidthChange,
        handleColumnsOrderChange,
        loading,
        error,
        query,
        choices
      }
    }>
      {children}
    </EmployeesContext.Provider>
  );
};
